import React, { useState, useEffect, useRef } from 'react';
import type { NewUserFormData, PlatformUser, UserRole, UserStatus } from '../../types/user';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { SearchableSelect } from '../../../../components/ui/SearchableSelect';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { Mail, Phone, Building2, Building, Lock, RefreshCw, Plus, Edit, Eye, EyeOff, Loader2, Shield, ArrowLeft } from 'lucide-react';
import { ROLE_PERMISSIONS } from '../../constants/permissions';
import { AddUserCategory, CreateUser, getListUserCategory, updateUser } from '../../../../redux/UserManagement/saga';
import { PASSWORD_PATTERN } from '../../../../utils/constants';
import { generateStrongPassword } from '../../../../utils/generateStrongPassword';
import AddNewItemDialog from '../../../../components/ui/AddNewItemDialog';
import { resetUserPassword } from '../../../../redux/UserManagement/saga';
import { Notification as customNotification } from '../../../../components/ui/custom-notification';
import PhoneNumberField from './PhoneNumberField';
import axiosConfig from '../../../../redux/UserManagement/axiosConfig';
import { parseUserExtraDetails } from '../../utils/parseUserExtraDetails';
import {
  buildUserExtraDetailsPayload,
  userExtraDetailsToFormState,
} from '../../utils/userFormExtraPermissionsState';

// Define types for API response
interface DepartmentApiResponse {
  department_id: string;
  department_name: string;
}

interface CompanyApiResponse {
  company_id: string;
  company_name: string;
}

interface RoleApiResponse {
  app_level_role_id: number;
  group_name: string;
  permissions: Record<string, any>;
  is_active: boolean;
  display_name: string;
}

interface Permission {
  id: string;
  name: string;
}

interface UserFormProps {
  user?: PlatformUser;
  onSubmit: (updateResult?: { id?: string; user_extra_details?: PlatformUser['user_extra_details'] }) => void | Promise<void>;
  onCancel: () => void;
  onBack?: () => void;
  loading?: boolean;
  isEdit?: boolean;
  resetPasswordModalVisible: boolean;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  onBack,
  loading = false,
  isEdit = false,
  resetPasswordModalVisible = false
}) => {
  const [formData, setFormData] = React.useState({
    firstName: isEdit || resetPasswordModalVisible ? user?.firstName || '' : '',
    lastName: isEdit || resetPasswordModalVisible ? user?.lastName || '' : '',
    email: isEdit || resetPasswordModalVisible ? user?.email || '' : '',
    phoneNumber: isEdit || resetPasswordModalVisible ? user?.phone || '' : '',
    department: isEdit || resetPasswordModalVisible ? user?.profile.department?.id || '' : '',
    company: isEdit || resetPasswordModalVisible ? user?.profile.company?.id || '' : '',
    initialPassword: isEdit || resetPasswordModalVisible ? '' : '',
    role: isEdit || resetPasswordModalVisible ? user?.role || '' : '',
    sendWelcomeEmail: false
  });

  const [isAddingRole, setIsAddingRole] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [isAddingDepartment, setIsAddingDepartment] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showAllPermissions, setShowAllPermissions] = useState(false);
  const [showProjectAdvanced, setShowProjectAdvanced] = useState(false);
  const [showAnalysisAdvanced, setShowAnalysisAdvanced] = useState(false);
  const [showPipeInventoryAdvanced, setShowPipeInventoryAdvanced] = useState(false);
  const [showMapAdvanced, setShowMapAdvanced] = useState(false);
  const [showDevicesAdvanced, setShowDevicesAdvanced] = useState(false);
  const [selectedProjectPermissions, setSelectedProjectPermissions] = useState<Set<string>>(new Set());
  const [selectedAnalysisPermissions, setSelectedAnalysisPermissions] = useState<Set<string>>(new Set());
  const [selectedPipeInventoryPermissions, setSelectedPipeInventoryPermissions] = useState<Set<string>>(new Set());
  const [selectedMapPermissions, setSelectedMapPermissions] = useState<Set<string>>(new Set());
  const [selectedDevicesPermissions, setSelectedDevicesPermissions] = useState<Set<string>>(new Set());
  const [departments, setDepartments] = useState<DepartmentApiResponse[]>([]);
  const [companies, setCompanies] = useState<CompanyApiResponse[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [roles, setRoles] = useState<RoleApiResponse[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<string>('strong');  // 'strong', 'medium', 'weak'
  /** Invite / welcome-link expiry — same options as forgot-password reset. Default 24 hours. */
  const [inviteExpirySeconds, setInviteExpirySeconds] = useState<number>(86400);

  // Password validation states
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordPolicy, setShowPasswordPolicy] = useState(false);
  const [passwordValidationStatus, setPasswordValidationStatus] = useState<'validating' | 'success' | 'warning' | ''>('');

  // Search states
  const [roleSearch, setRoleSearch] = useState('');

  interface ValidationStatus {
    password: 'validating' | 'success' | 'warning' | '';
  }

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoadingData(true);
        
        // Load departments, companies, and roles in parallel
        const [departmentsData, companiesData, rolesData] = await Promise.all([
          getListUserCategory('department'),
          getListUserCategory('company'),
          getListUserCategory('role')
        ]);

        
        // Remove duplicates and sort companies alphabetically by company_name
        const uniqueCompanies = companiesData
          .filter((company: any, index: number, self: any[]) => 
            index === self.findIndex((c: any) => c.company_name === company.company_name)
          )
          .sort((a: any, b: any) => a.company_name.localeCompare(b.company_name));
        
        // Remove duplicates and sort departments alphabetically by department_name
        const uniqueDepartments = departmentsData
          .filter((department: any, index: number, self: any[]) => 
            index === self.findIndex((d: any) => d.department_name === department.department_name)
          )
          .sort((a: any, b: any) => a.department_name.localeCompare(b.department_name));
        
        // Remove duplicates and sort roles alphabetically by group_name
        const uniqueRoles = rolesData
          .filter((role: any, index: number, self: any[]) => 
            index === self.findIndex((r: any) => r.group_name === role.group_name)
          )
          .sort((a: any, b: any) => a.group_name.localeCompare(b.group_name));
            
        
        // Set data directly without duplicate removal
        setDepartments(uniqueDepartments || []);
        setCompanies(uniqueCompanies || []);
        setRoles(uniqueRoles || []);

        if (isEdit && user) {
          // Find the department and company objects from the loaded data
          const userDepartment = departmentsData.find((dept: any) => dept.department_id === user?.profile.department?.id);
          const userCompany = companiesData.find((company: any) => company.company_id === user?.profile.company?.id);
          const userRole = rolesData.find((role: any) => role.group_name === user?.role);

          // Set the selected IDs
          setSelectedDepartmentId(user?.profile.department?.id || '');
          setSelectedCompanyId(user?.profile.company?.id || '');
          setSelectedRoleId(userRole?.app_level_role_id?.toString() || '');

          // Update form data with user values
          setFormData(prev => ({
            ...prev,
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            phoneNumber: user?.phone || '',
            department: userDepartment?.department_name || '',
            company: userCompany?.company_name || '',
            role: user?.role || '',
            initialPassword: '', // Don't populate password for edit mode
            sendWelcomeEmail: false
          }));

          if (user?.role === 'user' || user?.role === 'viewer' || user?.role === 'manager') {
            let parsedExtra = parseUserExtraDetails(user.user_extra_details);
            if (user.cognitoUserId) {
              try {
                const detailRes = await axiosConfig.get(
                  `/user/getUserByCognitoId/${user.cognitoUserId}`
                );
                const detailUser = detailRes.data?.data;
                const fromServer = parseUserExtraDetails(
                  detailUser?.user_extra_details ?? detailUser?.userExtraDetails
                );
                if (fromServer) {
                  parsedExtra = fromServer;
                }
              } catch {
                /* fall back to list row extras */
              }
            }

            const permState = userExtraDetailsToFormState(parsedExtra);
            setSelectedProjectPermissions(new Set(permState.project));
            setSelectedAnalysisPermissions(new Set(permState.analysis));
            setSelectedPipeInventoryPermissions(new Set(permState.pipeInventory));
            setSelectedMapPermissions(new Set(permState.map));
            setSelectedDevicesPermissions(new Set(permState.devices));
            setShowProjectAdvanced(permState.showProjectAdvanced);
            setShowAnalysisAdvanced(permState.showAnalysisAdvanced);
            setShowPipeInventoryAdvanced(permState.showPipeInventoryAdvanced);
            setShowMapAdvanced(permState.showMapAdvanced);
            setShowDevicesAdvanced(permState.showDevicesAdvanced);
          }
        }

        setIsLoadingData(false);

      } catch (error) {
        setDepartments([]);
        setCompanies([]);
        setRoles([]);
        setIsLoadingData(false);
      }
    };
    if (!resetPasswordModalVisible)
      loadInitialData();
  }, [user, isEdit]);

  const skipRolePermissionClearRef = useRef(true);
  useEffect(() => {
    skipRolePermissionClearRef.current = true;
  }, [user?.id]);

  // Clear selected permissions that role already has when role changes (not on first load / edit hydrate)
  useEffect(() => {
    if (formData.role) {
      if (skipRolePermissionClearRef.current) {
        skipRolePermissionClearRef.current = false;
        return;
      }
      const rolePermissionIds = getRolePermissionIds();
      
      // Clear project permissions that role already has
      setSelectedProjectPermissions(prev => {
        const filtered = new Set(prev);
        projectPermissions.forEach(p => {
          if (rolePermissionIds.has(p.id)) {
            filtered.delete(p.id);
          }
        });
        return filtered;
      });

      // Clear analysis permissions that role already has
      setSelectedAnalysisPermissions(prev => {
        const filtered = new Set(prev);
        analysisPermissions.forEach(p => {
          if (rolePermissionIds.has(p.id)) {
            filtered.delete(p.id);
          }
        });
        return filtered;
      });

      setSelectedPipeInventoryPermissions(prev => {
        const filtered = new Set(prev);
        pipeInventoryPermissions.forEach(p => {
          if (rolePermissionIds.has(p.id)) {
            filtered.delete(p.id);
          }
        });
        return filtered;
      });

      // Clear map permissions that role already has
      setSelectedMapPermissions(prev => {
        const filtered = new Set(prev);
        mapPermissions.forEach(p => {
          if (rolePermissionIds.has(p.id)) {
            filtered.delete(p.id);
          }
        });
        return filtered;
      });

      // Clear devices permissions that role already has
      setSelectedDevicesPermissions(prev => {
        const filtered = new Set(prev);
        devicesPermissions.forEach(p => {
          if (rolePermissionIds.has(p.id)) {
            filtered.delete(p.id);
          }
        });
        return filtered;
      });
    }
  }, [formData.role]);



  const [errors, setErrors] = useState({
    email: '',
    phoneNumber: ''
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) return ''; // Phone is optional
    // Remove all non-digit characters except the leading +
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    // Check if it's a valid international phone number
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      return 'Please enter a valid phone number (10-15 digits)';
    }
    // Basic validation for international format
    if (!cleanPhone.startsWith('+') && cleanPhone.length < 10) {
      return 'Please enter a valid phone number';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordValidationStatus('');
      setShowPasswordPolicy(false);
      return;
    }

    setPasswordValidationStatus('validating');
    setShowPasswordPolicy(true);

   // setTimeout(() => {
      // Determine password strength based on actual password
      let strength: 'strong' | 'medium' | 'weak' = 'weak';
      
      if (password.length >= 12 && PASSWORD_PATTERN.test(password)) {
        strength = 'strong';
      } else if (password.length >= 8 && password.length < 12 && PASSWORD_PATTERN.test(password)) {
        strength = 'medium';
      } else {
        strength = 'weak';
      }

      setPasswordStrength(strength);

      if (strength === 'strong') {
        setPasswordValidationStatus('success');
        setShowPasswordPolicy(false);
      } else {
        setPasswordValidationStatus('warning');
      }
   // }, );
  };

  // Create-user: initial password is auto-generated (UI field is hidden).
  useEffect(() => {
    if (isEdit || resetPasswordModalVisible) return;
    const password = generateStrongPassword();
    setFormData((prev) => ({ ...prev, initialPassword: password }));
    validatePassword(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on create form mount
  }, [isEdit, resetPasswordModalVisible]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));
    setErrors(prev => ({ ...prev, email: validateEmail(email) }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setFormData(prev => ({ ...prev, phoneNumber: phone }));
    setErrors(prev => ({ ...prev, phoneNumber: validatePhoneNumber(phone) }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, initialPassword: value }));
    validatePassword(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhoneNumber(formData.phoneNumber);

    setErrors({
      email: emailError,
      phoneNumber: phoneError
    });

    if (emailError || phoneError) {
      return;
    }

    if (formData.firstName.trim() === '') {
      customNotification.warning('First name is required', 'Please enter the user\'s first name to continue.');
      return;
    }
    if (formData.lastName.trim() === '') {
      customNotification.warning('Last name is required', 'Please enter the user\'s last name to continue.');
      return;
    }

    // Validate password strength
    if (!isEdit && formData.initialPassword) {
      if (formData.initialPassword.length < 12 || !PASSWORD_PATTERN.test(formData.initialPassword)) {
        customNotification.warning('Password requirements not met', 
          'Password must be at least 12 characters long and contain uppercase, lowercase, number, and special character.');
        return;
      }
    }

    if (formData.email.trim() === '') {
      customNotification.warning('Email is required', 'Please enter a valid email address for the user.');
      return;
    }

    // Last name is optional, so no validation needed here
    if(selectedDepartmentId === ''){
      customNotification.warning('Department is required', 'Please select a department for the user.');
      return;
    }
    if(selectedCompanyId === ''){
      customNotification.warning('Company is required', 'Please select a company for the user.');
      return;
    }
    if(selectedRoleId === ''){
      customNotification.warning('Role is required', 'Please select a role for the user.');
      return;
    }
  

    setIsLoading(true);

    const userExtraDetailsPayload = buildUserExtraDetailsPayload({
      project: selectedProjectPermissions,
      analysis: selectedAnalysisPermissions,
      pipeInventory: selectedPipeInventoryPermissions,
      map: selectedMapPermissions,
      devices: selectedDevicesPermissions,
    });

    if (isEdit && user) {
      const data = {
        "id": user?.id,
        "firstName": formData.firstName,
        "lastName": formData.lastName || '', // Handle empty last name
        //"email": formData.email,
        "phoneNumber": formData.phoneNumber,
        "departmentId": selectedDepartmentId,
        "companyId": selectedCompanyId,
        "roleId": selectedRoleId,
        "sendEmail": false,//formData.sendWelcomeEmail,
        //"password": formData.initialPassword
        user_extra_details: userExtraDetailsPayload,
        userExtraDetails: userExtraDetailsPayload,
      }
      const res = await updateUser(data);
      setIsLoading(false);
      if (res !== null) {
        await onSubmit(res);
      }
    }
    else {
      // Password UI is hidden — always mint a fresh policy-compliant password at submit
      // so createUser never receives empty / stale / invalid credentials.
      const createPassword = generateStrongPassword();
      setFormData((prev) => ({ ...prev, initialPassword: createPassword }));

      const data = {
        "firstName": formData.firstName,
        "lastName": formData.lastName || '', // Handle empty last name
        "email": formData.email,
        "phoneNumber": formData.phoneNumber || '',
        "departmentId": selectedDepartmentId,
        "companyId": selectedCompanyId,
        "roleId": selectedRoleId,
        "sendEmail": false,//formData.sendWelcomeEmail,
        // Same ttl field the invite/forgot-password API accepts (seconds).
        "ttlSeconds": inviteExpirySeconds,
        user_extra_details: userExtraDetailsPayload,
        userExtraDetails: userExtraDetailsPayload,
        "password": createPassword,
      }

      const res = await CreateUser(data);
      
      // Send invite email after successful user creation
      if (formData.sendWelcomeEmail && res !== null) {
        try {
          const platform1 = "Data360";
          const ttlSeconds = inviteExpirySeconds;
          const inviteRes = await fetch(`${process.env.REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email, ttlSeconds, platform1 }),
          });

          if (inviteRes.status === 200) {
            customNotification.success(
              'Invite Sent',
              'The invitation link has been successfully sent to the user.'
            );
          } else if (inviteRes.status === 403) {
            customNotification.error(
              'Unauthorized',
              'You do not have permission to access this resource.'
            );
          } else {
            const errorText = await inviteRes.text();
            customNotification.error('Failed to send invite', errorText || 'Unknown error');
          }
        } catch (error: any) {
          customNotification.error('Failed to send invite', error.message);
        }
      }
      
      setIsLoading(false);
      if (res !== null) {
        onSubmit();
      }
    }

  }

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password strength
    if (!isEdit && formData.initialPassword) {
      if (formData.initialPassword.length < 12 || !PASSWORD_PATTERN.test(formData.initialPassword)) {
        customNotification.warning('Password requirements not met', 'Password must be at least 12 characters long and contain uppercase, lowercase, number, and special character.');
        return;
      }
    }


    if (formData.initialPassword.trim() === '') {
    //  setErrors(prev => ({ ...prev, initialPassword: 'Password is required' }));
      customNotification.warning('Password is required', 'Please enter a password for the user.');
      return;
    }

    setIsLoading(true);


    const data = {
      "id": user?.id,
      "email": formData.email,
      "password": formData.initialPassword,
      "fromAdminPanel": true
    }
    const res = await resetUserPassword(data);
    setIsLoading(false);
    await onSubmit();

  };

  // Helper function to get permissions for a specific role
  const getRolePermissions = (roleName: string): Permission[] => {
    const roleData = ROLE_PERMISSIONS.find((r: any) => r.group_name === roleName);
    return roleData?.permissions?.[roleName] || [];
  };

  const currentRolePermissions = getRolePermissions(formData.role);
  const halfLength = Math.ceil(currentRolePermissions.length / 2);
  const leftColumnPermissions = currentRolePermissions.slice(0, halfLength);
  const rightColumnPermissions = currentRolePermissions.slice(halfLength);

  // Define permissions for each section
  const projectPermissions: Permission[] = [
    { id: 'create_projects', name: 'Create Projects' },
    { id: 'edit_projects', name: 'Edit Projects' },
    // { id: 'view_projects', name: 'View All Projects' },
    { id: 'delete_projects', name: 'Delete Projects' }
  ];

  const analysisPermissions: Permission[] = [
    { id: 'view_analytics', name: 'View Analytics' },
    { id: 'export_analytics', name: 'Export Analytics' },
  ];

  const pipeInventoryPermissions: Permission[] = [
    { id: 'view_pipe_inventory', name: 'View' },
    { id: 'edit_pipe_inventory', name: 'Add / Edit' },
    { id: 'delete_pipe_inventory', name: 'Delete' },
  ];

  const mapPermissions: Permission[] = [
    { id: 'view_map', name: 'View Map' }
  ];

  const devicesPermissions: Permission[] = [
    { id: 'view_devices', name: 'View Devices' },
    { id: 'manage_devices', name: 'Manage Devices' }
  ];

  // Helper function to get role's existing permission IDs
  const getRolePermissionIds = () => {
    if (!formData.role) return new Set<string>();
    const rolePerms = getRolePermissions(formData.role);
    return new Set(rolePerms.map((p: Permission) => p.id));
  };

  // Helper functions to get filtered permissions (only those role doesn't already have)
  const getFilteredProjectPermissions = () => {
    const rolePermissionIds = getRolePermissionIds();
    return projectPermissions.filter(p => !rolePermissionIds.has(p.id));
  };

  const getFilteredAnalysisPermissions = () => {
    const rolePermissionIds = getRolePermissionIds();
    return analysisPermissions.filter(p => !rolePermissionIds.has(p.id));
  };

  const getFilteredPipeInventoryPermissions = () => {
    const rolePermissionIds = getRolePermissionIds();
    return pipeInventoryPermissions.filter(p => !rolePermissionIds.has(p.id));
  };

  const getFilteredMapPermissions = () => {
    const rolePermissionIds = getRolePermissionIds();
    return mapPermissions.filter(p => !rolePermissionIds.has(p.id));
  };

  const getFilteredDevicesPermissions = () => {
    const rolePermissionIds = getRolePermissionIds();
    return devicesPermissions.filter(p => !rolePermissionIds.has(p.id));
  };

  // Helper functions to check if role already has all permissions in a section
  const roleHasAllProjectPermissions = () => {
    return getFilteredProjectPermissions().length === 0;
  };

  const roleHasAllAnalysisPermissions = () => {
    return getFilteredAnalysisPermissions().length === 0;
  };

  const roleHasAllPipeInventoryPermissions = () => {
    return getFilteredPipeInventoryPermissions().length === 0;
  };

  const roleHasAllMapPermissions = () => {
    return getFilteredMapPermissions().length === 0;
  };

  const roleHasAllDevicesPermissions = () => {
    return getFilteredDevicesPermissions().length === 0;
  };

  // Helper functions to check if all children are selected (using filtered permissions)
  const areAllProjectPermissionsSelected = () => {
    const filtered = getFilteredProjectPermissions();
    return filtered.length > 0 && filtered.every(permission => selectedProjectPermissions.has(permission.id));
  };

  const areAllAnalysisPermissionsSelected = () => {
    const filtered = getFilteredAnalysisPermissions();
    return filtered.length > 0 && filtered.every(permission => selectedAnalysisPermissions.has(permission.id));
  };

  const areAllPipeInventoryPermissionsSelected = () => {
    const filtered = getFilteredPipeInventoryPermissions();
    return filtered.length > 0 && filtered.every(permission => selectedPipeInventoryPermissions.has(permission.id));
  };

  const areAllMapPermissionsSelected = () => {
    const filtered = getFilteredMapPermissions();
    return filtered.length > 0 && filtered.every(permission => selectedMapPermissions.has(permission.id));
  };

  const areAllDevicesPermissionsSelected = () => {
    const filtered = getFilteredDevicesPermissions();
    return filtered.length > 0 && filtered.every(permission => selectedDevicesPermissions.has(permission.id));
  };

  // Handlers for parent checkbox changes (using filtered permissions)
  const handleProjectParentChange = (checked: boolean) => {
    setShowProjectAdvanced(checked);
    if (checked) {
      // Select all filtered project permissions (only those role doesn't have)
      const filtered = getFilteredProjectPermissions();
      setSelectedProjectPermissions(new Set(filtered.map(p => p.id)));
    } else {
      // Deselect all project permissions
      setSelectedProjectPermissions(new Set());
    }
  };

  const handleAnalysisParentChange = (checked: boolean) => {
    setShowAnalysisAdvanced(checked);
    if (checked) {
      // Select all filtered analysis permissions (only those role doesn't have)
      const filtered = getFilteredAnalysisPermissions();
      setSelectedAnalysisPermissions(new Set(filtered.map(p => p.id)));
    } else {
      // Deselect all analysis permissions
      setSelectedAnalysisPermissions(new Set());
    }
  };

  const handlePipeInventoryParentChange = (checked: boolean) => {
    setShowPipeInventoryAdvanced(checked);
    if (checked) {
      const filtered = getFilteredPipeInventoryPermissions();
      setSelectedPipeInventoryPermissions(new Set(filtered.map(p => p.id)));
    } else {
      setSelectedPipeInventoryPermissions(new Set());
    }
  };

  const handleMapParentChange = (checked: boolean) => {
    setShowMapAdvanced(checked);
    if (checked) {
      // Select all filtered map permissions (only those role doesn't have)
      const filtered = getFilteredMapPermissions();
      setSelectedMapPermissions(new Set(filtered.map(p => p.id)));
    } else {
      // Deselect all map permissions
      setSelectedMapPermissions(new Set());
    }
  };

  const handleDevicesParentChange = (checked: boolean) => {
    setShowDevicesAdvanced(checked);
    if (checked) {
      // Select all filtered devices permissions (only those role doesn't have)
      const filtered = getFilteredDevicesPermissions();
      setSelectedDevicesPermissions(new Set(filtered.map(p => p.id)));
    } else {
      // Deselect all devices permissions
      setSelectedDevicesPermissions(new Set());
    }
  };

  const handleAddRole = () => {
    if (newRole.trim()) {
      setFormData(prev => ({ ...prev, role: newRole.toLowerCase().replace(/\s+/g, '_') as UserRole }));
      setNewRole('');
      setIsAddingRole(false);
    }
  };

  const handleAddCompany = async (companyName: string) => {
    const newCompanyData = {
      company_name: companyName.trim(),
    };
    const res = await AddUserCategory(newCompanyData, false);
    // Refresh companies data after successful addition
    const updatedCompanies = await getListUserCategory('company');
    setCompanies(updatedCompanies);
    // Find the newly added company and set its ID
    const newCompanyItem = updatedCompanies.find((company: CompanyApiResponse) => company.company_name === companyName);
    if (newCompanyItem) {
      setSelectedCompanyId(newCompanyItem.company_id);
      setFormData(prev => ({ ...prev, company: newCompanyItem.company_name }));
    }
  };

  const handleAddDepartment = async (departmentName: string) => {
    const newDepartmentData = {
      department_name: departmentName.trim(),
    };
    const res = await AddUserCategory(newDepartmentData, true);
    // Refresh departments data after successful addition
    const updatedDepartments = await getListUserCategory('department');
    setDepartments(updatedDepartments);
    // Find the newly added department and set its ID
    const newDepartmentItem = updatedDepartments.find((dept: DepartmentApiResponse) => dept.department_name === departmentName);
    if (newDepartmentItem) {
      setSelectedDepartmentId(newDepartmentItem.department_id);
      setFormData(prev => ({ ...prev, department: newDepartmentItem.department_name }));
    }
  };

  // Clear search when select is closed
  const clearSearch = () => {
    setRoleSearch('');
  };


  const getValidationIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <div className="absolute inset-y-0 right-14 flex items-center">
          <div className="h-5 w-5 text-green-500">✓</div>
        </div>;
      case 'warning':
        return <div className="absolute inset-y-0 right-14 flex items-center">
          <Shield className="h-5 w-5 text-red-500" />
        </div>;
      case 'validating':
        return <div className="absolute inset-y-0 right-14 flex items-center">
          <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
        </div>;
      default:
        return null;
    }
  };

  const getInputBorderColor = (status: string) => {
    switch (status) {
      case 'success':
        return ' focus:';
      case 'warning':
        return ' focus:';
      case 'validating':
        return ' focus:';
      default:
        return 'border-gray-300 focus:border-[#EB1C37]';
    }
  };

  // Determine the title based on the mode
  const getTitle = () => {
    if (resetPasswordModalVisible) {
      return 'Reset Password';
    }
    return isEdit ? 'Edit User' : 'Add New User';
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onCancel();
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            variant="outline"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Button>
          <h1 className="text-2xl font-bold">{getTitle()}</h1>
        </div>

        {resetPasswordModalVisible ? (
          <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
          {/* Hidden fields to prevent browser auto-fill */}
          <div style={{ display: 'none' }}>
            <input type="text" name="fakeusernameremembered" title="hidden" placeholder="hidden" />
            <input type="password" name="fakepasswordremembered" title="hidden" placeholder="hidden" />
          </div>
          {/* Row 1: Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                placeholder="John"
                className="mt-1"
                disabled={loading || resetPasswordModalVisible}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Doe"
                className="mt-1"
                required
                disabled={loading || resetPasswordModalVisible}
              />
            </div>
          </div>

          {/* Row 2: Email and Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input disabled={resetPasswordModalVisible}
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleEmailChange}
                  required
                  placeholder="john.doe@example.com"
                  className={`pl-9 ${errors.email ? '' : ''}`}
                  autoComplete="off"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="resetPassword">Reset Password <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="resetPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.initialPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Enter initial password"
                  className={`pl-9 pr-20 ${getInputBorderColor(passwordValidationStatus)} [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-credentials-auto-fill-button]:hover:bg-none [&::-webkit-credentials-auto-fill-button]:active:bg-none [&::-webkit-credentials-auto-fill-button]:focus:bg-none`}
                  disabled={loading || isEdit}
                  autoComplete="new-password"
                  onFocus={() => {
                    if (passwordValidationStatus !== 'success') {
                      setShowPasswordPolicy(true);
                    }
                  }}
                  onBlur={() => {
                    if (passwordValidationStatus !== 'success') {
                      setTimeout(() => setShowPasswordPolicy(false), 200);
                    }
                  }}
                />
                {/* Eye and Refresh buttons container */}
                <div className="absolute right-3 top-0 bottom-0 my-auto flex items-center space-x-1 h-10" style={{ height: '2.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <Button disabled={isEdit}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      // Generate password that always meets strong criteria: length >= 12 and PASSWORD_PATTERN (lower, upper, digit, special, no spaces)
                      const lower = 'abcdefghijklmnopqrstuvwxyz';
                      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                      const digits = '0123456789';
                      const special = '!@#$%^&*';
                      const all = lower + upper + digits + special;
                      const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
                      const required = [pick(lower), pick(upper), pick(digits), pick(special)];
                      const rest = Array.from({ length: 8 }, () => pick(all));
                      const shuffled = [...required, ...rest].sort(() => Math.random() - 0.5);
                      const password = shuffled.join('');
                      setFormData(prev => ({ ...prev, initialPassword: password }));
                      validatePassword(password);
                      setShowPassword(!showPassword);
                    }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {/* Password Strength Bar */}
           
              </div>
              { formData.initialPassword.length > 0 && <div className="mt-2 flex items-center">
                  {[0, 1, 2, 3, 4].map((idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 w-8 mx-0.5 rounded transition-colors duration-300 ${
                        passwordStrength === 'strong'
                          ? 'bg-green-500'
                          : passwordStrength === 'medium'
                          ? idx < 3 ? 'bg-yellow-400' : 'bg-gray-200'
                          : idx < 1 ? 'bg-red-500' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                  <span className={`ml-2 text-xs font-medium ${
                    passwordStrength === 'strong'
                      ? 'text-green-600'
                      : passwordStrength === 'medium'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}>
                    {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                  </span>
                </div>
              }
              {showPasswordPolicy && formData.initialPassword.length > 0 && passwordValidationStatus !== 'success' && (
                <div className="mt-2 p-3 bg-blue-50 border rounded-md">
                  <h6 className="text-sm font-medium text-blue-900 mb-2">Password requirements:</h6>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Minimum length of 12 character(s)</li>
                    <li>• Contains at least 1 number</li>
                    <li>• Contains at least 1 special character</li>
                    <li>• Contains at least 1 uppercase letter</li>
                    <li>• Contains at least 1 lowercase letter</li>
                  </ul>
                </div>
              )}
            </div>
          </div>




          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
            loading={isLoading}
            loadingText="Resetting password..."
            type="submit" className="bg-red-500 hover:bg-red-600 text-white" disabled={isLoading} >
              {'Reset Password'}
            </Button>
          </div>
        </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Hidden fields to prevent browser auto-fill */}
          <div style={{ display: 'none' }}>
            <input type="text" name="fakeusernameremembered" title="hidden" placeholder="hidden" />
            <input type="password" name="fakepasswordremembered" title="hidden" placeholder="hidden" />
          </div>
          {/* Row 1: Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                placeholder="Enter First Name"
                className="mt-1"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Enter Last Name"
                className="mt-1"
                disabled={loading}
                required
              />
            </div>
          </div>

          {/* Row 2: Email and Phone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleEmailChange}
                  required
                  placeholder="john.doe@example.com"
                  className={`pl-9 ${errors.email ? '' : ''}`}
                  disabled={loading || isEdit}
                  autoComplete="off"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <div className="mt-1">
                <PhoneNumberField
                  value={formData.phoneNumber}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, phoneNumber: value }));
                    setErrors(prev => ({ ...prev, phoneNumber: validatePhoneNumber(value) }));
                  }}
                  error={errors.phoneNumber}
                  disabled={loading}
                  required={true}
                  placeholder="Enter phone number"
                  defaultCountry="US"
                />
              </div>
            </div>
          </div>

          {/* Row 3: Department and Company */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="department">Department <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.department}
                  onValueChange={(value: string) => {
                    const selectedDept = departments.find(dept => dept.department_name === value);
                    if (selectedDept) {
                      setSelectedDepartmentId(selectedDept.department_id);
                      setFormData(prev => ({ ...prev, department: value }));
                    }
                  }}
                  placeholder="Select or enter department"
                  searchPlaceholder="Search departments..."
                  options={departments.map(dept => ({ id: dept.department_id, name: dept.department_name }))}
                  onAddNew={() => setIsAddingDepartment(true)}
                  addNewText="Add New Department"
                  icon={<Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                  disabled={isLoadingData || loading}
                  loading={isLoadingData}
                  loadingText="Loading departments..."
                />
              </div>
            </div>
            <div>
              <Label htmlFor="company">Company <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={formData.company}
                  onValueChange={(value: string) => {
                    const selectedComp = companies.find(company => company.company_name === value);
                    if (selectedComp) {
                      setSelectedCompanyId(selectedComp.company_id);
                      setFormData(prev => ({ ...prev, company: value }));
                    }
                  }}
                  placeholder="Select or enter company name"
                  searchPlaceholder="Search companies..."
                  options={companies.map(company => ({ id: company.company_id, name: company.company_name }))}
                  onAddNew={() => setIsAddingCompany(true)}
                  addNewText="Add New Company"
                  icon={<Building className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                  disabled={isLoadingData || loading}
                  loading={isLoadingData}
                  loadingText="Loading companies..."
                />
              </div>
            </div>
          </div>

          {/* Row 4: Role (Initial Password UI hidden on create — password auto-generated) */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                <Select
                  value={formData.role}
                  onValueChange={(value) => {
                    const selectedRole = roles.find(role => role.group_name === value);
                    if (selectedRole) {
                      setSelectedRoleId(selectedRole.app_level_role_id.toString());
                      setFormData(prev => ({ ...prev, role: value as UserRole }));
                      setRoleSearch(''); // Clear search when value is selected
                    }
                  }}
                  disabled={isLoadingData || loading}
                >
                  <SelectTrigger className="pl-9">
                    <SelectValue placeholder={isLoadingData ? "Loading roles..." : "Select role"}>

                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {isLoadingData ? (
                      <div className="p-2 text-center text-gray-500">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          <span>Loading roles...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-200 flex-shrink-0 h-12">
                          <Input
                            placeholder="Search roles..."
                            value={roleSearch}
                            onChange={(e) => setRoleSearch(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0 max-h-56">
                          {roles
                            .filter(role => 
                              role.group_name.toLowerCase().includes(roleSearch.toLowerCase()) ||
                              (role.group_name === 'admin' ? 'Admin' : role.group_name === 'super_admin' ? 'Super Admin' : role.group_name === 'manager' ? 'Manager' : role.group_name === 'user' ? 'User' : role.group_name === 'viewer' ? 'Viewer' : role.group_name).toLowerCase().includes(roleSearch.toLowerCase())
                            )
                            .map(role => (
                              <SelectItem key={role.app_level_role_id} value={role.group_name}>
                                {role.group_name === 'admin' ? 'Admin' : role.group_name === 'super_admin' ? 'Super Admin' : role.group_name === 'manager' ? 'Manager' : role.group_name === 'user' ? 'User' : role.group_name === 'viewer' ? 'Viewer' : role.group_name}
                              </SelectItem>
                            ))}
                        </div>
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Welcome Email Checkbox + Link Expiry (create only — same as forgot-password) */}
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Checkbox disabled={isEdit}
                  id="sendWelcomeEmail"
                  checked={formData.sendWelcomeEmail}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, sendWelcomeEmail: checked as boolean }))
                  }
                />
                <Label htmlFor="sendWelcomeEmail" className="text-sm font-normal">
                  Send welcome email with login credentials
                </Label>
              </div>
              <p className="text-sm text-gray-500 ml-6">
                User will receive an invite link to set their password
              </p>
            </div>

            {!isEdit && formData.sendWelcomeEmail && (
              <div className="ml-6">
                <Label htmlFor="inviteExpirySeconds" className="block text-sm font-medium text-gray-700 mb-2">
                  Link Expiry Time
                </Label>
                <Select
                  value={inviteExpirySeconds.toString()}
                  onValueChange={(value) => setInviteExpirySeconds(Number(value))}
                  disabled={loading || isLoading}
                >
                  <SelectTrigger id="inviteExpirySeconds" className="w-fit">
                    <SelectValue placeholder="Select Expiry Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1800">30 minutes</SelectItem>
                    <SelectItem value="3600">1 hour</SelectItem>
                    <SelectItem value="86400">24 hours</SelectItem>
                    <SelectItem value="172800">2 Days</SelectItem>
                    <SelectItem value="604800">7 Days</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1.5">
                  How long the invite link stays valid. Default is 24 hours.
                </p>
              </div>
            )}
          </div>

        

          {/* Role Permissions */}
          <div>
            <div
              className="flex items-center justify-between cursor-pointer mb-1 hover:bg-gray-50 p-2 rounded-md transition-colors"
              onClick={() => setShowPermissions(!showPermissions)}
            >
              <Label className={`text-sm font-normal ${showPermissions ? 'text-gray-900' : 'text-gray-500'} flex items-center gap-2`}>
                Role Permissions
                <span className="text-xs text-gray-400">(click to {showPermissions ? 'hide' : 'show'})</span>
              </Label>
            </div>
            {showPermissions && (
              <div className="grid grid-cols-2 gap-2 bg-gray-50 p-3 rounded-lg">
                <div className="space-y-1.5">
                  {leftColumnPermissions.map((permission: Permission) => (
                    <div key={permission.id} className="flex items-center space-x-2">
                      <span className="h-2 w-2 bg-red-500 rounded-full" />
                      <span className="text-sm">{permission.name}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5">
                  {rightColumnPermissions.map((permission: Permission) => (
                    <div key={permission.id} className="flex items-center space-x-2">
                      <span className="h-2 w-2 bg-red-500 rounded-full" />
                      <span className="text-sm">{permission.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Advanced Permissions Sections - Only visible for user, viewer, and manager roles */}
          {(formData.role === 'user' || formData.role === 'viewer' || formData.role === 'manager') && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {!roleHasAllProjectPermissions() && (
          <div className={`rounded-lg border border-gray-200 p-3 ${showProjectAdvanced ? 'sm:col-span-2' : ''}`}>
            <div className="flex min-h-8 items-center gap-2">
              <Checkbox 
                id="showProjectAdvanced"
                checked={showProjectAdvanced && selectedProjectPermissions.size > 0}
                onCheckedChange={(checked) => handleProjectParentChange(checked as boolean)}
              />
              <Label htmlFor="showProjectAdvanced" className="cursor-pointer text-sm font-semibold leading-none text-gray-900">Project</Label>
            </div>
            {showProjectAdvanced && (
            <div className="mt-3 rounded-lg bg-gray-50 p-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {getFilteredProjectPermissions().map((permission: Permission) => (
                  <div key={permission.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={`project-${permission.id}`}
                      checked={selectedProjectPermissions.has(permission.id)}
                      onCheckedChange={(checked) => {
                        const newSet = new Set(selectedProjectPermissions);
                        if (checked) {
                          newSet.add(permission.id);
                        } else {
                          newSet.delete(permission.id);
                        }
                        setSelectedProjectPermissions(newSet);
                      }}
                    />
                    <Label htmlFor={`project-${permission.id}`} className="cursor-pointer text-sm leading-none">
                      {permission.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
          )}

          {!roleHasAllAnalysisPermissions() && (
            <div className={`rounded-lg border border-gray-200 p-3 ${showAnalysisAdvanced ? 'sm:col-span-2' : ''}`}>
              <div className="flex min-h-8 items-center gap-2">
                <Checkbox 
                  id="showAnalysisAdvanced" 
                  checked={showAnalysisAdvanced && selectedAnalysisPermissions.size > 0} 
                  onCheckedChange={(checked) => handleAnalysisParentChange(checked as boolean)} 
                />
                <Label htmlFor="showAnalysisAdvanced" className="cursor-pointer text-sm font-semibold leading-none text-gray-900">Analysis</Label>
              </div>
              {showAnalysisAdvanced && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {getFilteredAnalysisPermissions().map((permission: Permission) => (
                      <div key={permission.id} className="flex items-center gap-2">
                        <Checkbox 
                          id={`analysis-${permission.id}`}
                          checked={selectedAnalysisPermissions.has(permission.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedAnalysisPermissions);
                            if (checked) {
                              newSet.add(permission.id);
                            } else {
                              newSet.delete(permission.id);
                            }
                            setSelectedAnalysisPermissions(newSet);
                          }}
                        />
                        <Label htmlFor={`analysis-${permission.id}`} className="cursor-pointer text-sm leading-none">
                          {permission.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!roleHasAllPipeInventoryPermissions() && (
            <div className={`rounded-lg border border-gray-200 p-3 ${showPipeInventoryAdvanced ? 'sm:col-span-2' : ''}`}>
              <div className="flex min-h-8 items-center gap-2">
                <Checkbox
                  id="showPipeInventoryAdvanced"
                  checked={showPipeInventoryAdvanced && selectedPipeInventoryPermissions.size > 0}
                  onCheckedChange={(checked) => handlePipeInventoryParentChange(checked as boolean)}
                />
                <Label htmlFor="showPipeInventoryAdvanced" className="cursor-pointer text-sm font-semibold leading-none text-gray-900">
                  Pipe Inventory
                </Label>
              </div>
              {showPipeInventoryAdvanced && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                    {getFilteredPipeInventoryPermissions().map((permission: Permission) => (
                      <div key={permission.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`pipe-inventory-${permission.id}`}
                          checked={selectedPipeInventoryPermissions.has(permission.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedPipeInventoryPermissions);
                            if (checked) {
                              newSet.add(permission.id);
                            } else {
                              newSet.delete(permission.id);
                            }
                            setSelectedPipeInventoryPermissions(newSet);
                          }}
                        />
                        <Label htmlFor={`pipe-inventory-${permission.id}`} className="cursor-pointer text-sm leading-none">
                          {permission.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!roleHasAllMapPermissions() && (
            <div className={`rounded-lg border border-gray-200 p-3 ${showMapAdvanced ? 'sm:col-span-2' : ''}`}>
              <div className="flex min-h-8 items-center gap-2">
                <Checkbox 
                  id="showMapAdvanced" 
                  checked={showMapAdvanced && selectedMapPermissions.size > 0} 
                  onCheckedChange={(checked) => handleMapParentChange(checked as boolean)} 
                />
                <Label htmlFor="showMapAdvanced" className="cursor-pointer text-sm font-semibold leading-none text-gray-900">Map</Label>
              </div>
              {showMapAdvanced && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {getFilteredMapPermissions().map((permission: Permission) => (
                      <div key={permission.id} className="flex items-center gap-2">
                        <Checkbox 
                          id={`map-${permission.id}`}
                          checked={selectedMapPermissions.has(permission.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedMapPermissions);
                            if (checked) {
                              newSet.add(permission.id);
                            } else {
                              newSet.delete(permission.id);
                            }
                            setSelectedMapPermissions(newSet);
                          }}
                        />
                        <Label htmlFor={`map-${permission.id}`} className="cursor-pointer text-sm leading-none">
                          {permission.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!roleHasAllDevicesPermissions() && (
            <div className={`rounded-lg border border-gray-200 p-3 ${showDevicesAdvanced ? 'sm:col-span-2' : ''}`}>
              <div className="flex min-h-8 items-center gap-2">
                <Checkbox 
                  id="showDevicesAdvanced" 
                  checked={showDevicesAdvanced && selectedDevicesPermissions.size > 0} 
                  onCheckedChange={(checked) => handleDevicesParentChange(checked as boolean)} 
                />
                <Label htmlFor="showDevicesAdvanced" className="cursor-pointer text-sm font-semibold leading-none text-gray-900">Devices</Label>
              </div>
              {showDevicesAdvanced && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {getFilteredDevicesPermissions().map((permission: Permission) => (
                      <div key={permission.id} className="flex items-center gap-2">
                        <Checkbox 
                          id={`devices-${permission.id}`}
                          checked={selectedDevicesPermissions.has(permission.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedDevicesPermissions);
                            if (checked) {
                              newSet.add(permission.id);
                            } else {
                              newSet.delete(permission.id);
                            }
                            setSelectedDevicesPermissions(newSet);
                          }}
                        />
                        <Label htmlFor={`devices-${permission.id}`} className="cursor-pointer text-sm leading-none">
                          {permission.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
            loading={isLoading}
            loadingText="Creating user..."
            type="submit" className="bg-red-500 hover:bg-red-600 text-white" disabled={isLoading} >
              {isEdit ? 'Update User' : 'Add User'}
            </Button>
          </div>
        </form>
        )}
      </div>

      <AddNewItemDialog
        open={isAddingCompany}
        onOpenChange={setIsAddingCompany}
        title="Add New Company"
        label="Company Name"
        placeholder="Enter company name"
        onAdd={handleAddCompany}
      />
      <AddNewItemDialog
        open={isAddingDepartment}
        onOpenChange={setIsAddingDepartment}
        title="Add New Department"
        label="Department Name"
        placeholder="Enter department name"
        onAdd={handleAddDepartment}
      />
    </div>
  );
}; 